import { SECTION_NAMES } from '@constants/sectionNames';
import { getNumberString } from '@fourthwall/components';
import { getPaymentMethodLabel, useAnalyticsChartQuery, useMembershipsChartQuery, useMembershipsTopTiersTableQuery, usePaymentMethodUsageTableQuery, useSaleByCountryFullTableQuery, useShopsStatsTrafficQuery, useTopOfferFullTableQuery, } from '@fourthwall/module-analytics';
import { getPrice } from '@fourthwall/utils';
import { downloadCsvFile } from '@fourthwall/utils/dist/downloadCsvFile';
import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop';
import { useCurrentShopQuery } from '@modules/Dashboard/hooks/useCurrentShopQuery';
import { useMembershipAccountQuery } from '@modules/Dashboard/hooks/useMembershipAccountQuery';
import { routing } from '@utils/routing';
import lowerCase from 'lodash-es/lowerCase';
import startCase from 'lodash-es/startCase';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { REPORT_TYPES, title } from '../../../../../consts';
import { useReportType } from '../../../../../hooks/useReportType';
import { createChart } from '../utils/createChart';
import { createStatsTable } from '../utils/createStatsTable';
import { getCsvData } from '../utils/getCsvData';
import { getExternalSourceBottomContent } from '../utils/getExternalSourceBottomContent';
export const useAnalyticsReport = (params) => {
    const { currentShopQuery } = useCurrentShopQuery();
    const { reportType } = useReportType();
    const location = useLocation();
    const { endpoint, formatCsv } = REPORT_TYPES[reportType];
    const { membershipAccountQuery } = useMembershipAccountQuery();
    const { currentShop } = useCurrentShop({ staleTime: Infinity });
    const { analyticsChartQuery } = useAnalyticsChartQuery([
        {
            ...params,
            chartTypes: [
                'CONTRIBUTION',
                'CONTRIBUTION_AVG',
                'CONTRIBUTION_TOTAL', // Contributions (Money)
                'CONTRIBUTION_ORDERS', // Contributions (number)
                'CONTRIBUTION_DONATIONS', // Contributions (number)
                'CONTRIBUTION_GIVEAWAY', // Contributions (number)
                // 'CONTRIBUTION_GIFTS_FROM_GIVEAWAY', // Contributions (number)
                'CONTRIBUTION_GIFTS_ON_CREATOR_COST', // Contributions (number)
                'CONTRIBUTION_SUBSCRIPTIONS', // Contributions (number)
                'CONTRIBUTION_MEMBERSHIP_TIPS',
                'CONTRIBUTION_MEMBERSHIP_LOCKED_MESSAGES',
                'PROFIT',
                // 'PROFIT_ORDERS', // Contributions (Money)
                // 'PROFIT_DONATIONS', // Contributions (Money)
                // 'PROFIT_GIFTS_FROM_GIVEAWAY', // Contributions (Money)
                // 'PROFIT_GIFTS_ON_CREATOR_COST', // Contributions (Money)
                // 'PROFIT_SUBSCRIPTIONS', // Contributions (Money)
                'PROFIT_MEMBERSHIP_TIPS',
                'PROFIT_MEMBERSHIP_LOCKED_MESSAGES',
                'GROSS_SALES',
                'INCOME',
                'COST',
                'CANCELLATIONS',
                'REFUNDS',
                'DISCOUNTS',
                'AVERAGE_SALES', // Average order value
                'AVERAGE_PROFIT', // Average order value
            ],
            sid: [currentShop?.id],
        },
    ], {
        keepPreviousData: true,
        enabled: endpoint === 'chart',
    });
    const { membershipsChartQuery } = useMembershipsChartQuery([
        {
            ...params,
            chartTypes: [
                'MEMBERS_TOTAL',
                'FREE_ACCOUNTS_TOTAL',
                'MEMBERS_CHANGE',
                'FREE_ACCOUNTS_CHANGE',
            ],
            sid: [currentShop?.id],
        },
    ], {
        keepPreviousData: true,
        enabled: endpoint === 'membership-chart' && !!currentShopQuery.data?.settings.memberships?.enabled,
    });
    const { topOfferFullTableQuery } = useTopOfferFullTableQuery([{ ...params, sid: [currentShop?.id] }], {
        keepPreviousData: true,
        enabled: endpoint === 'top-offer',
    });
    const { saleByCountryFullTableQuery } = useSaleByCountryFullTableQuery([{ ...params, sid: [currentShop?.id] }], {
        keepPreviousData: true,
        enabled: endpoint === 'top-sale-by-country',
    });
    const { membershipsTopTiersTableQuery } = useMembershipsTopTiersTableQuery([{ ...params, sid: [currentShop?.id] }], {
        enabled: !!currentShopQuery.data?.settings.memberships?.enabled,
    });
    const { paymentMethodUsageTableQuery } = usePaymentMethodUsageTableQuery([{ ...params, sid: [currentShop?.id] }], {
        keepPreviousData: true,
        enabled: endpoint === 'payment-method-used',
    });
    const { shopsStatsTrafficQuery } = useShopsStatsTrafficQuery([{ ...params }], {
        keepPreviousData: true,
        enabled: endpoint === 'shops-stats-traffic',
    });
    const backButtonTo = {
        pathname: routing.analytics.overview.self,
        search: location.search,
    };
    const chart = useMemo(() => createChart(analyticsChartQuery, membershipsChartQuery, shopsStatsTrafficQuery, params, reportType), [analyticsChartQuery, membershipsChartQuery, params, reportType, shopsStatsTrafficQuery]);
    const analyticsBarChart = (() => {
        if (reportType === 'payment-method-used') {
            return {
                data: paymentMethodUsageTableQuery.data?.current.data || [],
                // @ts-ignore
                getLabel: getPaymentMethodLabel,
                // @ts-ignore
                getValue: (item) => item.percentage,
                // @ts-ignore
                getValueLabel: (item) => `${item.percentage}%`,
                totalValue: paymentMethodUsageTableQuery.data?.current.totalValue || 100,
            };
        }
        if (reportType === 'sales-by-traffic-source') {
            return {
                data: shopsStatsTrafficQuery.data?.current.salesByTrafficSourceTable?.data || [],
                // @ts-ignore
                getLabel: (item) => startCase(lowerCase(item.source)),
                // @ts-ignore
                getValue: (item) => item.income,
                // @ts-ignore
                getValueLabel: (item) => {
                    // @ts-ignore
                    return getPrice(item.income, {
                        currency: shopsStatsTrafficQuery.data?.current.salesByTrafficSourceTable?.types?.currency,
                    });
                },
                totalValue: shopsStatsTrafficQuery.data?.current.salesByTrafficSourceTable?.totalValue,
            };
        }
        if (reportType === 'sessions-by-traffic-source') {
            return {
                data: shopsStatsTrafficQuery.data?.current.sessionsByTrafficSource?.data || [],
                // @ts-ignore
                getLabel: (item) => startCase(lowerCase(item.source)),
                // @ts-ignore
                getValue: (item) => item.count,
                // @ts-ignore
                getValueLabel: (item) => getNumberString(item.count),
                totalValue: shopsStatsTrafficQuery.data?.current.sessionsByTrafficSource?.totalValue,
            };
        }
        if (reportType === 'sales-by-social-source') {
            return {
                data: shopsStatsTrafficQuery.data?.current.salesBySocialSourceTable?.data || [],
                // @ts-ignore
                getLabel: (item) => startCase(lowerCase(item.source)),
                // @ts-ignore
                getValue: (item) => item.income,
                // @ts-ignore
                getValueLabel: (item) => {
                    // @ts-ignore
                    return getPrice(item.income, {
                        currency: shopsStatsTrafficQuery.data?.current.salesBySocialSourceTable?.types?.currency,
                    });
                },
                totalValue: shopsStatsTrafficQuery.data?.current.salesBySocialSourceTable?.totalValue,
            };
        }
        if (reportType === 'sessions-by-social-source') {
            return {
                data: shopsStatsTrafficQuery.data?.current.sessionsBySocialSource?.data || [],
                // @ts-ignore
                getLabel: (item) => startCase(lowerCase(item.source)),
                // @ts-ignore
                getValue: (item) => item.count,
                // @ts-ignore
                getValueLabel: (item) => getNumberString(item.count),
                totalValue: shopsStatsTrafficQuery.data?.current.sessionsBySocialSource?.totalValue,
            };
        }
        return undefined;
    })();
    const statsTable = useMemo(() => createStatsTable(reportType, params, analyticsChartQuery, membershipsChartQuery, topOfferFullTableQuery, saleByCountryFullTableQuery, membershipsTopTiersTableQuery, membershipAccountQuery, paymentMethodUsageTableQuery, shopsStatsTrafficQuery), [
        reportType,
        params,
        analyticsChartQuery,
        membershipsChartQuery,
        topOfferFullTableQuery,
        saleByCountryFullTableQuery,
        membershipsTopTiersTableQuery,
        membershipAccountQuery,
        paymentMethodUsageTableQuery,
        shopsStatsTrafficQuery,
    ]);
    const getExportButton = () => {
        const formattedData = formatCsv ? formatCsv(statsTable.data) : statsTable.data;
        const data = getCsvData(formattedData);
        return {
            enabled: formattedData.length > 0,
            onConfirm: () => downloadCsvFile(data, title.toLocaleLowerCase()),
        };
    };
    const getBottomContent = () => {
        if (REPORT_TYPES[reportType].externalSource === 'google-analytics') {
            const uri = {
                'conversion-rates': shopsStatsTrafficQuery.data?.current.conversionRates?.gaReferenceUri,
                'unique-visitors': shopsStatsTrafficQuery.data?.current.uniqueVisitors?.gaReferenceUri,
                'sales-by-traffic-source': shopsStatsTrafficQuery.data?.current.salesByTrafficSourceTable?.gaReferenceUri,
                'sessions-by-traffic-source': shopsStatsTrafficQuery.data?.current.sessionsByTrafficSource?.gaReferenceUri,
                'sales-by-social-source': shopsStatsTrafficQuery.data?.current.salesBySocialSourceTable?.gaReferenceUri,
                'sessions-by-social-source': shopsStatsTrafficQuery.data?.current.sessionsBySocialSource?.gaReferenceUri,
            };
            if (reportType in uri) {
                return getExternalSourceBottomContent('google-analytics', uri[reportType]);
            }
        }
    };
    const getBanner = () => {
        if (REPORT_TYPES[reportType].externalSource === 'google-analytics' &&
            !shopsStatsTrafficQuery.data?.current.gaConnectedBeforeFromDate) {
            return {
                title: 'This data may be inaccurate due to Google Analytics being connected during or after the selected date range',
                appearance: 'warning',
            };
        }
    };
    return {
        title: REPORT_TYPES[reportType].title,
        bottomContent: getBottomContent(),
        banner: getBanner(),
        backButtonLabel: SECTION_NAMES.analytics,
        backButtonTo,
        exportButton: getExportButton(),
        chart,
        analyticsBarChart,
        statsTable,
    };
};
